<!-- CertCardPrintModal — 실물카드 단건 인쇄 모달 (SPEC-CHINA-CERT-CARD-PRINT-001) -->
<!-- XR160 PRO 카드 프린터(CR80, 흑색 리본, 기제작 컬러 카드지 위 가변데이터 오버레이 전용) 인쇄.
     실제로 인쇄되는 면은 카드 "뒷면"뿐이고 뒷면 레이아웃은 디자인 버전과 무관하게 고정이므로
     (2026-08-11 사용자 피드백), 이 모달은 카드 디자인(버전) 개념 자체를 갖지 않는다. 미리보기는
     고정 뒷면 참조 이미지(card-back-reference.png, CR80 실척비 646x408) 위에 PRINT_BACK_LAYOUT
     고정 좌표로 오버레이한다. -->
<!-- [2026-08-13 UX 정리] 세 진입점(수기WO 완료 모달·제작이력 드로워·정품조회 드로워)이 전부 이
     컴포넌트를 공유하므로 여기 한 곳만 고치면 세 화면에 동시 적용된다.
     1) GTIN·No.(난수)는 카드지에 이미 인쇄되어 나온 값이다 — 미리보기에는 위치 확인용으로 남기되
        실제 인쇄 대상에서는 제외한다(덧인쇄하면 겹쳐 찍힌다). PRINTED_ON_CARD_KEYS 참조.
     2) "인쇄 레이어 미리보기"(흰 배경 검정 텍스트 박스)는 참조 이미지 미리보기와 좌표가 동일해
        정보량이 0이면서 혼란만 주므로 제거했다. 오프셋 시각 피드백은 참조 이미지 오버레이 레이어
        자체를 translate 하는 것으로 대체한다(ccpm-overlay-shift).
     3) 인쇄와 "인쇄 완료 표시"는 원래 한 트랜잭션(실제 출력 → 상태 반영)을 두 버튼으로 쪼개 둔
        것이라 하나로 합쳤다. 인쇄 대화상자가 닫힌 뒤 확인창에서 예를 눌러야만 printed로 표기한다
        (브라우저는 사용자가 인쇄를 취소했는지 알 수 없으므로 자동 표기는 위양성을 만든다). -->
<!-- 사용법: <cert-card-print-modal v-model:open="printModal.open" :card="printModal.card" @printed="onPrinted" /> -->
<template>
  <!-- [2026-10-15 배치] 넓은 화면(뷰포트 1000px 이상)은 2단: 왼쪽 미리보기(스크롤해도 고정) + 오른쪽
       정보·보정. 위치 조정 중에도 미리보기가 같은 화면에 보이게 하기 위함 — 예전 1단에서는 미리보기(위)와
       보정 폼(아래) 사이에 정보 표 7행이 끼어 X·Y를 바꿔도 움직임이 안 보였다.
       좁은 화면은 1단으로 되돌리고 미리보기 → 보정 → 정보 순서로 쌓으며 미리보기를 위에 고정한다. -->
  <a-modal
    :open="open"
    :title="$t('certCard_print_modal_title')"
    :width="modalWidth"
    :footer="null"
    :style="{ top: '24px' }"
    :body-style="{ maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', padding: 0 }"
    destroy-on-close
    @cancel="handleCancel"
  >
    <div class="ccpm-body" :class="{ 'is-wide': isWide }">
      <!-- 왼쪽(넓은 화면) / 위쪽(좁은 화면) — 스크롤해도 고정 -->
      <div class="ccpm-preview-col">
        <div class="ccpm-field">
          <span class="ccpm-label">{{ $t('certCard_print_field_randomNo') }}</span>
          <span class="ccpm-mono">No.{{ card && card.randomNo }}</span>
        </div>
        <div class="ccpm-field">
          <span class="ccpm-label">{{ $t('certCard_print_field_gtin') }}</span>
          <span class="ccpm-mono">{{ displayGtin }}</span>
        </div>

        <!-- 카드 뒷면 미리보기 — 고정 참조 이미지 + PRINT_BACK_LAYOUT 고정 좌표 오버레이.
             디자인 버전 select 없음(뒷면은 버전 무관 고정 레이아웃).
             GTIN·No.는 카드지에 이미 인쇄된 값이므로 회색 점선 배지로 구분해 표시한다(인쇄 대상 아님).
             2단에서는 바깥 상자를 CSS로 1.25배 키운다 — 안쪽은 320px 그대로라 좌표·글자 크기·mm 환산
             (PREVIEW_REFERENCE_WIDTH_PX)이 바뀌지 않아 인쇄 결과에 영향이 없다. -->
        <div class="ccpm-preview-scale">
          <div class="card-preview-image-wrapper">
            <img :src="cardBackReferenceImg" alt="card back" class="card-preview-image" />
            <!-- 오프셋(mm)은 이 레이어 전체를 밀어 캘리브레이션 결과를 눈으로 확인할 수 있게 한다 -->
            <div class="ccpm-overlay-shift" :style="overlayShiftStyle">
              <!-- 개별 조정 중에는 선택한 칸을 강조하고, 미리보기의 칸을 눌러 바로 고를 수 있다 -->
              <span
                v-for="key in FIELD_KEYS"
                :key="'composite-' + key"
                class="overlay-field"
                :class="{ 'overlay-field-selected': offsetMode === 'field' && selectedFieldKey === key }"
                :style="fieldOverlayStyle(key)"
                @click="selectField(key)"
              >{{ fieldValue(key) }}</span>
            </div>
            <!-- 카드지 기인쇄 값 — 오프셋과 무관(우리가 찍는 게 아니다)하므로 shift 레이어 밖에 둔다 -->
            <span class="overlay-preprinted overlay-gtin" :style="gtinOverlayStyle">{{ displayGtin || '—' }}</span>
            <span class="overlay-preprinted overlay-random-no" :style="randomNoOverlayStyle">No.{{ (card && card.randomNo) || PREVIEW_RANDOM_NO }}</span>
          </div>
        </div>
        <div class="ccpm-preprinted-legend">
          <span class="ccpm-preprinted-swatch"></span>
          {{ $t('certCard_print_preprinted_legend') }}
        </div>
        <!-- 카드 삽입방향 도움말 — 프린터에 카드를 거꾸로 넣는 실수가 잦아 사진으로 안내한다.
             2단에서는 미리보기 아래가 비어 있어 안내 사진을 상시 노출하고, 1단에서는 위 고정 영역이
             너무 커지지 않게 버튼 → 팝업으로 둔다. -->
        <div v-if="isWide" class="ccpm-insert-guide">
          <!-- 세로 사진(509x900)이라 작은 썸네일로 두고, 누르면 기존 팝업으로 크게 본다.
               [함정] 왼쪽 단이 화면보다 길어지면 sticky 미리보기가 같이 스크롤돼 버린다 — 높이를 늘리지 말 것. -->
          <img
            :src="insertHelpImageUrl"
            :alt="$t('certCard_print_insert_help_title')"
            class="ccpm-insert-guide-thumb"
            @click="helpOpen = true"
          />
          <div class="ccpm-insert-guide-body">
            <div class="ccpm-insert-guide-title">
              <question-circle-outlined />
              {{ $t('certCard_print_insert_help_title') }}
            </div>
            <p class="ccpm-help-text">{{ $t('certCard_print_insert_help_body') }}</p>
          </div>
        </div>
        <div v-else class="ccpm-help-row">
          <a-button type="link" size="small" @click="helpOpen = true">
            <question-circle-outlined />
            {{ $t('certCard_print_insert_help_btn') }}
          </a-button>
        </div>
      </div>

      <!-- 작업지시서 정보 (읽기 전용 SSOT) — 입력/수정 UI 없음. linked=false면 인쇄 자체가 불가하다는 안내만. -->
      <div class="ccpm-info-col">
        <a-divider orientation="left" orientation-margin="0" class="ccpm-section-title">
          {{ $t('certCard_print_fields_section_title') }}
          <template v-if="printData && printData.linked">
            <a-tag v-if="printData.source === 'manual'" class="ccpm-source-tag">{{ $t('certCard_print_source_manual') }}</a-tag>
            <a-tag v-else-if="printData.source === 'hengjiu'" class="ccpm-source-tag">{{ $t('certCard_print_source_hengjiu') }}</a-tag>
          </template>
        </a-divider>
        <a-spin :spinning="printDataLoading">
          <a-alert
            v-if="printData && printData.linked === false"
            type="error"
            show-icon
            :message="$t('certCard_print_unlinked_notice')"
            style="margin-bottom: 12px;"
          />
          <a-descriptions
            v-else-if="printData && printData.linked"
            :column="1"
            bordered
            size="small"
            :label-style="{ width: '96px' }"
          >
            <a-descriptions-item v-for="key in FIELD_KEYS" :key="key" :label="fieldLabel(key)">
              {{ fieldValue(key) || '-' }}
            </a-descriptions-item>
          </a-descriptions>
        </a-spin>
      </div>

      <div class="ccpm-calib-col">
        <a-divider orientation="left" orientation-margin="0" class="ccpm-section-title">
          {{ $t('certCard_print_calibration_title') }}
        </a-divider>
        <!-- 현재 보정값 요약 — 값은 이 PC 브라우저에만 저장되므로 캡처해 두면 복구·원격 진단에 쓸 수 있다 -->
        <div class="ccpm-offset-summary">
          <span class="ccpm-offset-summary-title">{{ $t('certCard_print_offset_summary_title') }}</span>
          {{ offsetSummary }}
        </div>

        <!-- 보정 방식: 개별(기본, 칸 하나씩) / 일괄(7칸 전체). 두 값은 합산되므로 전환해도 값이 사라지지 않는다. -->
        <div class="ccpm-offset-mode">
          <a-radio-group v-model:value="offsetMode" button-style="solid" size="small">
            <a-radio-button value="field">{{ $t('certCard_print_offset_mode_field') }}</a-radio-button>
            <a-radio-button value="all">{{ $t('certCard_print_offset_mode_all') }}</a-radio-button>
          </a-radio-group>
          <div class="ccpm-offset-hint">
            {{ offsetMode === 'field' ? $t('certCard_print_offset_mode_field_hint') : $t('certCard_print_offset_mode_all_hint') }}
          </div>
        </div>

        <!-- 입력 컨트롤은 a-form-item 대신 위 라벨 + 컨트롤로 통일 — form-item 기본 여백(24px)·가로 라벨이
             섞여 간격이 들쭉날쭉했다. -->
        <div v-if="offsetMode === 'field'" class="ccpm-ctrl">
          <div class="ccpm-ctrl-label">{{ $t('certCard_print_offset_field_select') }}</div>
          <a-select v-model:value="selectedFieldKey" style="width: 100%;">
            <a-select-option v-for="key in FIELD_KEYS" :key="key" :value="key">
              {{ fieldLabel(key) }}
              <span v-if="hasFieldOffset(key)" class="ccpm-offset-badge">
                {{ formatOffset(fieldOffsets[key]) }}
              </span>
            </a-select-option>
          </a-select>
        </div>

        <!-- X·Y 입력 + 방향 버튼 — 칸별 모드는 선택한 칸, 일괄 모드는 7칸 전체가 대상 -->
        <div class="ccpm-offset-input-row">
          <div class="ccpm-ctrl ccpm-offset-input">
            <div class="ccpm-ctrl-label">{{ $t('certCard_print_offset_x') }}</div>
            <a-input-number
              v-if="offsetMode === 'field'"
              v-model:value="fieldOffsets[selectedFieldKey].x"
              :min="-FIELD_OFFSET_LIMIT_MM"
              :max="FIELD_OFFSET_LIMIT_MM"
              :step="0.1"
              style="width: 100%;"
              @change="persistOffset"
            />
            <a-input-number
              v-else
              v-model:value="offsetX"
              :min="-FIELD_OFFSET_LIMIT_MM"
              :max="FIELD_OFFSET_LIMIT_MM"
              :step="0.1"
              style="width: 100%;"
              @change="persistOffset"
            />
          </div>
          <div class="ccpm-ctrl ccpm-offset-input">
            <div class="ccpm-ctrl-label">{{ $t('certCard_print_offset_y') }}</div>
            <a-input-number
              v-if="offsetMode === 'field'"
              v-model:value="fieldOffsets[selectedFieldKey].y"
              :min="-FIELD_OFFSET_LIMIT_MM"
              :max="FIELD_OFFSET_LIMIT_MM"
              :step="0.1"
              style="width: 100%;"
              @change="persistOffset"
            />
            <a-input-number
              v-else
              v-model:value="offsetY"
              :min="-FIELD_OFFSET_LIMIT_MM"
              :max="FIELD_OFFSET_LIMIT_MM"
              :step="0.1"
              style="width: 100%;"
              @change="persistOffset"
            />
          </div>
          <a-tooltip :title="$t('certCard_print_offset_nudge_tooltip')">
            <div class="ccpm-nudge-pad">
              <a-button size="small" class="ccpm-nudge-up" @click="nudgeOffset(0, -1)"><arrow-up-outlined /></a-button>
              <a-button size="small" class="ccpm-nudge-left" @click="nudgeOffset(-1, 0)"><arrow-left-outlined /></a-button>
              <a-button size="small" class="ccpm-nudge-right" @click="nudgeOffset(1, 0)"><arrow-right-outlined /></a-button>
              <a-button size="small" class="ccpm-nudge-down" @click="nudgeOffset(0, 1)"><arrow-down-outlined /></a-button>
            </div>
          </a-tooltip>
        </div>

        <div v-if="offsetMode === 'field'" class="ccpm-offset-reset-row">
          <a-button size="small" @click="resetSelectedFieldOffset">{{ $t('certCard_print_offset_reset_field') }}</a-button>
          <a-button size="small" @click="resetAllFieldOffsets">{{ $t('certCard_print_offset_reset_all_fields') }}</a-button>
        </div>

        <!-- 일괄값이 남아 있으면 두 화면 모두에서 칸별값으로 옮길 수 있게 한다(인쇄 위치는 그대로) -->
        <div v-if="offsetX || offsetY" class="ccpm-offset-move-row">
          <span class="ccpm-offset-global-note">
            {{ $t('certCard_print_offset_global_applied') }} {{ formatOffset({ x: offsetX, y: offsetY }) }}
          </span>
          <a-tooltip :title="$t('certCard_print_offset_move_to_fields_tooltip')">
            <a-button size="small" @click="moveGlobalToFields">{{ $t('certCard_print_offset_move_to_fields') }}</a-button>
          </a-tooltip>
        </div>

        <a-alert
          type="info"
          show-icon
          :message="$t('certCard_print_hint')"
          style="margin-bottom: 4px;"
        />
      </div>
    </div>

    <!-- 버튼줄은 스크롤해도 아래에 고정 — 보정 폼을 펼쳐 둔 채로도 인쇄 버튼이 항상 보인다 -->
    <div class="ccpm-actions">
      <a-button @click="handleCancel">{{ $t('common_cancel') }}</a-button>

      <!-- 테스트 인쇄는 캘리브레이션 전용 — 실데이터/작업지시서 연결 여부와 무관하게 항상 활성.
           GTIN·No.가 인쇄 대상에서 빠졌으므로 7필드 자리에 더미 문자열을 찍어 위치를 확인한다. -->
      <a-tooltip :title="$t('certCard_print_test_btn_tooltip')">
        <a-button @click="handleTestPrint">
          {{ $t('certCard_print_test_btn') }}
          <question-circle-outlined class="ccpm-btn-help-ico" />
        </a-button>
      </a-tooltip>

      <a-tooltip :title="isLinked ? $t('certCard_print_btn_tooltip') : $t('certCard_print_unlinked_tooltip')">
        <span>
          <a-button
            type="primary"
            :loading="marking"
            :disabled="!card || !card.randomNo || !isLinked"
            @click="handlePrint"
          >
            {{ $t('certCard_print_btn') }}
            <question-circle-outlined class="ccpm-btn-help-ico" />
          </a-button>
        </span>
      </a-tooltip>
    </div>

    <!-- 인쇄 결과 확인 — 인쇄 대화상자가 닫힌 뒤에만 뜬다. 예를 눌러야 printed로 표기한다.
         [함정] Modal.confirm 정적 API는 오버레이가 남는 문제가 있어 컴포넌트로 띄운다. -->
    <a-modal
      :open="confirmOpen"
      :title="$t('certCard_print_confirm_title')"
      :ok-text="$t('certCard_print_confirm_ok')"
      :cancel-text="$t('certCard_print_confirm_cancel')"
      :confirm-loading="marking"
      :mask-closable="false"
      @ok="handleConfirmPrinted"
      @cancel="confirmOpen = false"
    >
      <p style="margin: 0;">{{ $t('certCard_print_confirm_body') }}</p>
    </a-modal>

    <!-- 카드 삽입방향 도움말 -->
    <a-modal
      :open="helpOpen"
      :title="$t('certCard_print_insert_help_title')"
      :footer="null"
      width="420px"
      @cancel="helpOpen = false"
    >
      <p class="ccpm-help-text">{{ $t('certCard_print_insert_help_body') }}</p>
      <img :src="insertHelpImageUrl" :alt="$t('certCard_print_insert_help_title')" class="ccpm-help-image" />
    </a-modal>
  </a-modal>
</template>

<script>
// @MX:NOTE: [AUTO] CertCardPrintModal — XR160 PRO 실물카드 단건 인쇄 모달.
// 뒷면 레이아웃은 디자인 버전과 무관하게 고정(PRINT_BACK_LAYOUT) — 디자인 select/조회 없음.
// 실제 인쇄는 배경 이미지 없이 작업지시서 7필드만 검정 텍스트로 CR80 실척(85.6mm x 54mm)에 오버레이한다.
// @MX:WARN: GTIN·randomNo는 카드지 기인쇄 값이라 절대 인쇄 HTML에 넣지 않는다.
// @MX:REASON: 덧인쇄하면 기존 인쇄물 위에 검정 텍스트가 겹쳐 찍혀 카드가 폐기된다.
import { message } from 'ant-design-vue';
import {
  QuestionCircleOutlined, ArrowUpOutlined, ArrowDownOutlined, ArrowLeftOutlined, ArrowRightOutlined,
} from '@ant-design/icons-vue';
import certificateCardApi from '@/apis/certificateCard';
import cardBackReferenceImg from '@/assets/img/certCard/card-back-reference.png';
import { fitScale } from './printFieldFit';
import { FIELD_OFFSET_LIMIT_MM, emptyFieldOffsets, normalizeFieldOffsets } from './printFieldOffset';

// CR80 카드 실척 (mm)
const CARD_WIDTH_MM = 85.6;
const CARD_HEIGHT_MM = 54;
// 미리보기 기준폭(px) — fontPx는 이 폭 기준으로 튜닝됨(카드 실척 85.6mm에 대응).
// 실물 인쇄 시 font-size(mm) = fontPx / PREVIEW_REFERENCE_WIDTH_PX * CARD_WIDTH_MM 로 환산한다.
const PREVIEW_REFERENCE_WIDTH_PX = 320;
// 오프셋(mm) localStorage 키 — 디자인 버전이 없어졌으므로 단일 키(디자인별 키 없음, 마이그레이션 불필요)
const OFFSET_STORAGE_KEY = 'certCardPrint.offset';
// 칸별 개별 보정값(mm) localStorage 키 — 일괄 보정 키와 따로 둔다(기존 일괄값을 건드리지 않기 위함).
// 형식: { patientName: { x, y }, ... } — 최종 위치 = 일괄값 + 칸별 값.
const FIELD_OFFSET_STORAGE_KEY = 'certCardPrint.fieldOffset';
// mm → 미리보기 px 환산 비율 (미리보기 폭 320px = 카드 실척 85.6mm)
const PREVIEW_PX_PER_MM = PREVIEW_REFERENCE_WIDTH_PX / CARD_WIDTH_MM;
// 테스트 인쇄용 더미 난수 (여분 카드 캘리브레이션 전용)
const PREVIEW_RANDOM_NO = '0000000000';

// 카드 삽입방향 안내 사진 — 가이드 자산과 같은 COS 공개읽기 버킷(ap-shanghai).
// VideoGuideButton.vue와 같은 이유로 COS를 쓴다: 시청자가 대부분 중국 내 기공소라 국내망이 빠르다.
const GUIDE_ASSET_BASE = 'https://dentalmax-guides-1326561935.cos.ap-shanghai.myqcloud.com';
const INSERT_HELP_IMAGE_PATH = '/images/xr160-card-insert-direction.jpg';

// 카드 뒷면 고정 레이아웃 (SPEC-CHINA-CERT-CARD-PRINT-001, 2026-08-11 뒷면 참조 이미지
// card-back-reference.png(646x408px, CR80 실척비 85.6:54와 동일 비율)에서 프로그래밍 방식으로
// 실측한 값). 앞/뒷면 합성 이미지가 아니라 뒷면 단독 이미지이므로 리맵이 필요 없다 — 여기 퍼센트가
// 곧 화면 미리보기 좌표이자 인쇄 좌표다(단일 소스, computeFacePositions류 변환 없음).
//
// 도출 근거:
//  - 라벨 박스 행(row) 상단 23.0/37.5/52.0/66.2%, 각 행 높이 ~10.6% → 텍스트 top = 박스 중앙 − 텍스트
//    높이의 절반(중앙정렬에 가깝게 보이도록 살짝 아래로).
//  - 좌측 컬럼 leftPct=18은 좌측 컬럼 최장 라벨 "医疗机构"(라벨 폭 끝 ~15.3%) 뒤에서 시작.
//  - 우측 컬럼 leftPct=65는 우측 컬럼 최장 라벨 "加工单位"(라벨 폭 끝 ~62.4%) 뒤에서 시작.
//  - gtin/randomNo 좌표는 인쇄 대상이 아니지만(카드지 기인쇄) 미리보기에서 위치를 보여주는 데 계속 쓴다.
const PRINT_BACK_LAYOUT = {
  gtin: { topPct: 9.4, rightPct: 5, widthPct: 27, fontPx: 8 },
  // randomNo.leftPct는 라벨의 "중앙" x좌표 — left:leftPct% + translateX(-50%)
  randomNo: { bottomPct: 7.3, leftPct: 50, fontPx: 10 },
  fields: {
    patientName: { topPct: 26.1, leftPct: 18 },
    doctorName: { topPct: 40.6, leftPct: 18 },
    date: { topPct: 55.0, leftPct: 18 },
    hospitalName: { topPct: 69.3, leftPct: 18 },
    // 牙位만 같은 줄 患者(26.1)보다 3.4%(약 1.8mm) 아래 — 오른쪽 위 무늬 스티커가 붙은 카드지는
    // 카드 높이 약 30.1%까지 잉크가 안 묻어(2026-10-13 실물 사진 측정) 글자 위쪽이 잘리거나, 긴 값이라
    // 작아지면 통째로 안 찍혔다. 0.6배로 줄어든 글자 윗선도 그 선 아래로 오게 했고, 대신 원래 크기
    // 글자는 회색 칸 아래 테두리를 약 0.6mm 넘는다.
    // ponytail: 카드지 한 종류에 맞춘 고정값 — 카드지가 더 생기면 칸 아래쪽 기준(기준선 고정) 배치로 바꾼다.
    toothPosition: { topPct: 29.5, leftPct: 65 },
    orderNo: { topPct: 40.6, leftPct: 65 },
    labName: { topPct: 55.0, leftPct: 65 },
  },
  fieldFontPx: 9,
  // 컬럼별 최대폭(면 전체 너비 대비 %) — 칸을 넘는 값은 printFieldFit.fitScale로 폰트를 줄여
  // 먼저 맞춰보고(최소 0.6배), 그래도 넘치면 overflow:hidden + nowrap로 자름(최후 수단)
  fieldMaxWidthPct: { L: 30, R: 30 },
};
const FIELD_KEYS = Object.keys(PRINT_BACK_LAYOUT.fields);
// 칸 여유마진(2026-10-12 실측 결함 수정) — 축소 없이 정확히 칸 폭(100%)까지 채우면 여유가
// 0이 되어 ①미리보기: .overlay-field padding 0 2px(border-box 전역 CSS)가 96px 박스에서
// 좌우 4px을 더 먹어(12자 기준 8.003px*12=96.03px + 4px > 96px) 말줄임(…)으로 잘려 보이고,
// ②인쇄: 패딩은 없지만 프린터 래스터라이즈 여유가 전혀 없어(12자*2.14mm=25.68mm=박스 그대로)
// 실물에서 삐져나올 위험이 있다. 패딩분(~4%) + 인쇄 여유분(~2%)을 더해 총 6% 할인한 폭을
// 수용 가능폭으로 쓴다 — 미리보기·인쇄 모두 이 할인된 폭을 공유해 어긋나지 않는다.
const FIELD_FIT_SAFETY = 0.94;
// 컬럼별 수용 가능폭(em) — fieldMaxWidthPct(%)를 미리보기 기준폭(px)/기본 폰트(px)로 환산한
// 값(≈10.67)에 FIELD_FIT_SAFETY(여유마진)를 곱해 실제 수용폭(≈10.03)으로 쓴다.
// L/R 둘 다 30%라 사실상 같은 값이지만 fieldMaxWidthPct가 늘어나도 그대로 대응하도록 컬럼별로 둔다.
const FIELD_MAX_EM = Object.fromEntries(
  Object.entries(PRINT_BACK_LAYOUT.fieldMaxWidthPct).map(([column, pct]) => (
    [column, (pct / 100 * PREVIEW_REFERENCE_WIDTH_PX) / PRINT_BACK_LAYOUT.fieldFontPx * FIELD_FIT_SAFETY]
  )),
);
// 소수 3자리 반올림 — font-size 계산값 표시용
const round3 = (n) => Math.round(n * 1000) / 1000;
// 2단 배치 기준 뷰포트 폭(px) — 이보다 좁으면 1단(480px)으로 되돌린다
const WIDE_LAYOUT_MIN_VIEWPORT_PX = 1000;
// 방향 버튼 한 번에 움직이는 거리(mm)
const NUDGE_STEP_MM = 0.1;
const clampOffsetMm = (n) => Math.min(FIELD_OFFSET_LIMIT_MM, Math.max(-FIELD_OFFSET_LIMIT_MM, round3(n)));

// 테스트 인쇄용 더미 7필드 — GTIN·No.가 인쇄 대상에서 빠져 실데이터 없이 인쇄하면 백지가 되므로,
// 캘리브레이션에서 위치를 눈으로 잡을 수 있도록 각 칸에 자리표시 문자열을 찍는다.
// 길이·형식은 실제 값과 비슷하게 둔다(2026-10-14) — 「00」 같은 짧은 더미로는 실제 값(예: 牙位 11자)의
// 위치·끝 잘림·흐림을 미리 볼 수 없어 기공소가 혼자 보정할 때 판단이 어긋난다.
const TEST_PRINT_FIELDS = {
  patientName: '样本姓名',
  doctorName: '样本医生',
  date: '2026/01/01',
  hospitalName: '样本口腔医院有限公司',
  toothPosition: '14-18,44-48',
  orderNo: '260101001',
  labName: '样本牙科技术有限公司',
};

export default {
  name: 'CertCardPrintModal',

  components: {
    QuestionCircleOutlined, ArrowUpOutlined, ArrowDownOutlined, ArrowLeftOutlined, ArrowRightOutlined,
  },

  props: {
    open: { type: Boolean, default: false },
    card: { type: Object, default: () => ({}) },
  },

  emits: ['update:open', 'printed'],

  data() {
    return {
      // 일괄 보정(mm) — 7칸 전체를 함께 이동
      offsetX: 0,
      offsetY: 0,
      // 칸별 개별 보정(mm) — 일괄 보정에 더해진다
      fieldOffsets: emptyFieldOffsets(FIELD_KEYS),
      // 보정 방식 화면: 'field'(개별, 기본) | 'all'(일괄). 입력칸 표시만 바꾸고 값은 둘 다 항상 적용된다.
      offsetMode: 'field',
      selectedFieldKey: FIELD_KEYS[0],
      marking: false,
      PREVIEW_RANDOM_NO,
      FIELD_KEYS,
      FIELD_OFFSET_LIMIT_MM,
      cardBackReferenceImg,
      insertHelpImageUrl: `${(process.env.VUE_APP_GUIDE_VIDEO_BASE_URL || GUIDE_ASSET_BASE).replace(/\/$/, '')}${INSERT_HELP_IMAGE_PATH}`,
      // 실물카드 인쇄용 데이터(작업지시서 7필드) — 읽기 전용 SSOT, BE에서만 옴. 입력 UI 없음.
      printData: null,
      printDataLoading: false,
      // 인쇄 대화상자가 닫힌 뒤 뜨는 "정상 출력됐습니까?" 확인창
      confirmOpen: false,
      helpOpen: false,
      // 2단/1단 전환용 — a-modal width는 CSS 미디어쿼리로 못 바꾸므로 resize로 추적한다
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : WIDE_LAYOUT_MIN_VIEWPORT_PX,
    };
  },

  mounted() {
    window.addEventListener('resize', this.onViewportResize);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.onViewportResize);
  },

  computed: {
    isWide() {
      return this.viewportWidth >= WIDE_LAYOUT_MIN_VIEWPORT_PX;
    },
    modalWidth() {
      return this.isWide ? '920px' : '480px';
    },
    // 뒷면 고정 좌표 — 참조 이미지 미리보기 전용(인쇄 HTML은 buildPrintHtml이 별도로 만든다)
    gtinOverlayStyle() {
      const o = PRINT_BACK_LAYOUT.gtin;
      return { top: `${o.topPct}%`, right: `${o.rightPct}%`, width: `${o.widthPct}%`, fontSize: `${o.fontPx}px` };
    },
    randomNoOverlayStyle() {
      const o = PRINT_BACK_LAYOUT.randomNo;
      return { bottom: `${o.bottomPct}%`, left: `${o.leftPct}%`, transform: 'translateX(-50%)', fontSize: `${o.fontPx}px` };
    },
    // 오프셋(mm) → px 환산. 미리보기 폭이 PREVIEW_REFERENCE_WIDTH_PX(=카드 실척 85.6mm)와 같으므로
    // buildPrintHtml의 mm translate와 동일한 비율로 보인다.
    overlayShiftStyle() {
      return { transform: `translate(${this.offsetX * PREVIEW_PX_PER_MM}px, ${this.offsetY * PREVIEW_PX_PER_MM}px)` };
    },

    // 현재 보정값 한 줄 요약 — 브라우저 저장값이 지워져도 캡처로 복구하고, 문의 시 캡처 한 장으로
    // 원격 진단할 수 있게 한다. 값이 있는 것만 나열한다.
    offsetSummary() {
      const parts = [];
      if (this.offsetX || this.offsetY) {
        parts.push(`${this.$t('certCard_print_offset_mode_all')} ${this.formatOffset({ x: this.offsetX, y: this.offsetY })}`);
      }
      FIELD_KEYS.forEach((key) => {
        const off = this.fieldOffsets && this.fieldOffsets[key];
        if (off && (off.x || off.y)) parts.push(`${this.fieldLabel(key)} ${this.formatOffset(off)}`);
      });
      return parts.length ? parts.join(' · ') : this.$t('certCard_print_offset_summary_none');
    },

    // GTIN 표시값 — card prop에 gtin이 없으면 print-data 응답의 gtin으로 폴백한다.
    // 진입점이 randomNo만 알아도(예: 수기 작업지시서 목록) 모달이 자기완결로 동작하게 하기 위함.
    displayGtin() {
      return (this.card && this.card.gtin) || (this.printData && this.printData.gtin) || '';
    },

    // BE print-data 응답이 linked:true를 명시적으로 반환했을 때만 인쇄 버튼 활성 (fail-closed)
    isLinked() {
      return !!(this.printData && this.printData.linked === true);
    },
  },

  watch: {
    open(val) {
      if (val) this.onOpen();
    },
  },

  methods: {
    async onOpen() {
      this.marking = false;
      this.printData = null;
      this.confirmOpen = false;
      this.helpOpen = false;
      this.restoreOffset();
      await this.loadPrintData();
    },

    // 작업지시서 연결 카드의 7필드 읽기 전용 데이터 조회. 실패 시 연결 여부를 판단할 수 없으므로
    // fail-closed(미연결 취급)로 인쇄를 막는다 — 절대 낙관적으로 인쇄 가능 취급하지 않는다.
    async loadPrintData() {
      if (!this.card || !this.card.randomNo) {
        this.printData = null;
        return;
      }
      this.printDataLoading = true;
      try {
        const res = await certificateCardApi.getCardPrintData(this.card.randomNo);
        this.printData = res.data?.data ?? res.data ?? null;
      } catch (e) {
        this.printData = { linked: false, source: null, fields: null };
        message.error(this.$t('certCard_print_data_load_error'));
      } finally {
        this.printDataLoading = false;
      }
    },

    fieldLabel(key) {
      return this.$t(`certCard_print_field_${key}`);
    },

    fieldValue(key) {
      return this.printData?.fields?.[key] || '';
    },

    // 작업지시서 7필드 — 뒷면 고정 좌표 오버레이 스타일 (참조 이미지 미리보기용)
    // 값이 칸 폭(maxEm)을 넘으면 fitScale로 폰트를 줄인다 — 화면에 보이는 값(fieldValue)
    // 기준으로 축소해야 실제 인쇄(buildPrintHtml)와 미리보기가 어긋나지 않는다.
    fieldOverlayStyle(key) {
      const layout = PRINT_BACK_LAYOUT.fields[key];
      if (!layout) return {};
      const column = layout.leftPct < 50 ? 'L' : 'R';
      const scale = fitScale(this.fieldValue(key), FIELD_MAX_EM[column]);
      const style = {
        top: `${layout.topPct}%`,
        left: `${layout.leftPct}%`,
        maxWidth: `${PRINT_BACK_LAYOUT.fieldMaxWidthPct[column]}%`,
        fontSize: `${round3(PRINT_BACK_LAYOUT.fieldFontPx * scale)}px`,
      };
      // 칸별 개별 보정 — 일괄 보정은 바깥 shift 레이어가 이미 옮겼으므로 여기선 이 칸 값만 더한다
      const off = this.fieldOffsets && this.fieldOffsets[key];
      if (off && (off.x || off.y)) {
        style.transform = `translate(${round3(off.x * PREVIEW_PX_PER_MM)}px, ${round3(off.y * PREVIEW_PX_PER_MM)}px)`;
      }
      return style;
    },

    selectField(key) {
      if (this.offsetMode === 'field') this.selectedFieldKey = key;
    },

    hasFieldOffset(key) {
      const off = this.fieldOffsets[key];
      return !!(off && (off.x || off.y));
    },

    // 드롭다운·안내문 표시용 "X 0.3 / Y -0.2"
    formatOffset(off) {
      return `X ${Number(off.x) || 0} / Y ${Number(off.y) || 0}`;
    },

    resetSelectedFieldOffset() {
      this.fieldOffsets[this.selectedFieldKey] = { x: 0, y: 0 };
      this.persistOffset();
    },

    // 개별값만 지운다 — 일괄값은 일괄 조정 화면에서 따로 관리한다
    resetAllFieldOffsets() {
      this.fieldOffsets = emptyFieldOffsets(FIELD_KEYS);
      this.persistOffset();
    },

    // 일괄값을 7칸 칸별값에 더하고 일괄값은 0으로 — 인쇄 위치는 그대로이고, 이후 칸 하나만 따로 고칠 수 있다.
    // 칸별 기능 이전에 일괄값을 넣어 둔 PC(예: 牙位를 내리려고 7칸 전체를 내린 경우)가 넘어오는 경로.
    onViewportResize() {
      this.viewportWidth = window.innerWidth;
    },

    // 방향 버튼 — dx/dy는 -1·0·1. Y+는 아래(인쇄 translate와 같은 방향). 칸별 모드는 선택한 칸,
    // 일괄 모드는 일괄값을 0.1mm 옮기고 바로 저장한다(입력칸을 비워 null이면 0에서 시작).
    nudgeOffset(dx, dy) {
      if (this.offsetMode === 'field') {
        const off = this.fieldOffsets[this.selectedFieldKey] || {};
        this.fieldOffsets[this.selectedFieldKey] = {
          x: clampOffsetMm((Number(off.x) || 0) + dx * NUDGE_STEP_MM),
          y: clampOffsetMm((Number(off.y) || 0) + dy * NUDGE_STEP_MM),
        };
      } else {
        this.offsetX = clampOffsetMm((Number(this.offsetX) || 0) + dx * NUDGE_STEP_MM);
        this.offsetY = clampOffsetMm((Number(this.offsetY) || 0) + dy * NUDGE_STEP_MM);
      }
      this.persistOffset();
    },

    moveGlobalToFields() {
      const moved = {};
      FIELD_KEYS.forEach((key) => {
        const off = (this.fieldOffsets && this.fieldOffsets[key]) || {};
        moved[key] = {
          x: round3((Number(off.x) || 0) + (Number(this.offsetX) || 0)),
          y: round3((Number(off.y) || 0) + (Number(this.offsetY) || 0)),
        };
      });
      this.fieldOffsets = normalizeFieldOffsets(moved, FIELD_KEYS);
      this.offsetX = 0;
      this.offsetY = 0;
      this.persistOffset();
    },

    restoreOffset() {
      this.offsetX = 0;
      this.offsetY = 0;
      try {
        const raw = window.localStorage.getItem(OFFSET_STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          this.offsetX = Number(parsed.x) || 0;
          this.offsetY = Number(parsed.y) || 0;
        }
      } catch (e) {
        // 파싱 실패 — 기본값으로 폴백
      }
      let rawFields = null;
      try {
        rawFields = JSON.parse(window.localStorage.getItem(FIELD_OFFSET_STORAGE_KEY));
      } catch (e) {
        // 파싱 실패 — 개별값 전부 0으로 폴백
      }
      this.fieldOffsets = normalizeFieldOffsets(rawFields, FIELD_KEYS);
    },

    persistOffset() {
      try {
        window.localStorage.setItem(OFFSET_STORAGE_KEY, JSON.stringify({ x: this.offsetX, y: this.offsetY }));
        window.localStorage.setItem(FIELD_OFFSET_STORAGE_KEY, JSON.stringify(this.fieldOffsets));
      } catch (e) {
        // 저장 실패(용량 초과 등) 무시 — 이번 세션 안에서는 값이 유지됨
      }
    },

    handleCancel() {
      this.$emit('update:open', false);
    },

    escapeHtml(str) {
      return String(str ?? '').replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ));
    },

    // 작업지시서 7필드만 검정색으로 오버레이한 인쇄용 HTML 문서 생성 (배경 이미지 없음).
    // GTIN·randomNo는 카드지에 이미 인쇄되어 나오는 값이라 여기서 절대 찍지 않는다(겹쳐 찍힘 방지).
    // 좌표는 PRINT_BACK_LAYOUT 고정값을 그대로 사용한다 — 화면 미리보기(fieldOverlayStyle)와
    // 동일 소스라 화면과 인쇄 결과가 어긋나지 않는다. 값이 칸 폭을 넘으면 원본(비escape) 값
    // 기준으로 fitScale을 구해 font-size(mm)를 줄인다 — escape된 문자열로 재면 &amp; 같은
    // 엔티티 길이 때문에 실제 글자 수보다 길게 측정되어 과도하게 축소될 수 있다.
    buildPrintHtml(fields) {
      const fieldFontMm = (PRINT_BACK_LAYOUT.fieldFontPx / PREVIEW_REFERENCE_WIDTH_PX) * CARD_WIDTH_MM;
      const fieldsHtml = fields
        ? FIELD_KEYS.map((key) => {
          const layout = PRINT_BACK_LAYOUT.fields[key];
          const rawValue = fields[key] || '';
          const value = this.escapeHtml(rawValue);
          if (!value) return '';
          const column = layout.leftPct < 50 ? 'L' : 'R';
          const scale = fitScale(rawValue, FIELD_MAX_EM[column]);
          // 칸별 개별 보정 — 일괄 보정(.shift)에 더해 이 칸만 추가로 민다
          const off = this.fieldOffsets && this.fieldOffsets[key];
          // 입력칸을 비우면 null이 오므로 0으로 본다
          const shift = off && (off.x || off.y) ? `transform:translate(${Number(off.x) || 0}mm, ${Number(off.y) || 0}mm); ` : '';
          return `<div style="position:absolute; top:${layout.topPct}%; left:${layout.leftPct}%; ${shift}`
            + `max-width:${PRINT_BACK_LAYOUT.fieldMaxWidthPct[column]}%; font-size:${round3(fieldFontMm * scale)}mm; `
            + `font-family: 'Microsoft YaHei', 'PingFang SC', sans-serif; font-weight:bold; color:#000; overflow:hidden; white-space:nowrap;">${value}</div>`;
        }).join('')
        : '';
      return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<style>
  @page { size: ${CARD_WIDTH_MM}mm ${CARD_HEIGHT_MM}mm; margin: 0; }
  html, body { margin: 0; padding: 0; }
  .card { position: relative; width: ${CARD_WIDTH_MM}mm; height: ${CARD_HEIGHT_MM}mm; overflow: hidden; }
  .shift { position: absolute; inset: 0; transform: translate(${this.offsetX}mm, ${this.offsetY}mm); }
</style>
</head>
<body>
  <div class="card">
    <div class="shift">
      ${fieldsHtml}
    </div>
  </div>
</body>
</html>`;
    },

    // 화면에 보이지 않는 iframe에 인쇄용 문서를 로드하고 브라우저 인쇄 대화상자를 연다.
    // onAfterPrint는 인쇄 대화상자가 닫힌 뒤(인쇄/취소 무관) 한 번만 호출된다 — 호출자가
    // "정상 출력됐습니까?" 확인창을 띄우는 지점이다. afterprint 미지원 환경에서는 호출되지 않는다.
    printViaIframe(html, onAfterPrint) {
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);

      let cleaned = false;
      const cleanup = () => {
        if (cleaned) return;
        cleaned = true;
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      };

      let notified = false;
      const notify = () => {
        if (notified) return;
        notified = true;
        if (typeof onAfterPrint === 'function') onAfterPrint();
      };

      iframe.onload = () => {
        const win = iframe.contentWindow;
        try {
          win.addEventListener('afterprint', () => {
            cleanup();
            notify();
          });
        } catch (e) {
          // afterprint 미지원 환경 — 아래 타임아웃 폴백으로 정리됨
        }
        try {
          win.focus();
          win.print();
          // print()는 대화상자가 닫힐 때까지 블로킹되는 브라우저가 대부분이다.
          // afterprint가 오지 않는 환경을 위한 보조 경로 — notify는 중복 호출되지 않는다.
          notify();
        } catch (e) {
          // 사용자가 인쇄 대화상자를 취소했거나 브라우저가 print()를 막은 경우 — 무시
        }
      };
      // afterprint 미지원/누락 대비 폴백 정리
      setTimeout(cleanup, 60000);
      iframe.srcdoc = html;
    },

    // 테스트 인쇄 — 캘리브레이션 전용, 작업지시서 연결 여부와 무관하게 항상 가능.
    // 실데이터 대신 더미 7필드를 찍는다(GTIN·No.는 인쇄 대상이 아니라 백지가 되므로).
    handleTestPrint() {
      this.persistOffset();
      this.printViaIframe(this.buildPrintHtml(TEST_PRINT_FIELDS));
    },

    // 인쇄 — 작업지시서 미연결 카드는 인쇄 불가(fail-closed, 버튼도 비활성이지만 방어적으로 재확인).
    // 인쇄 대화상자가 닫히면 확인창을 띄우고, 사용자가 "예"를 눌러야 printed로 표기한다.
    handlePrint() {
      if (!this.isLinked) return;
      this.persistOffset();
      this.printViaIframe(
        this.buildPrintHtml(this.printData?.fields || null),
        () => { this.confirmOpen = true; },
      );
    },

    async handleConfirmPrinted() {
      if (!this.card || !this.card.randomNo || !this.isLinked) return;
      this.marking = true;
      try {
        await certificateCardApi.markCardPrinted(this.card.randomNo);
        message.success(this.$t('certCard_print_mark_success'));
        this.confirmOpen = false;
        this.$emit('printed', { randomNo: this.card.randomNo, printedAt: new Date().toISOString() });
        this.$emit('update:open', false);
      } catch (e) {
        message.error(this.$t('certCard_print_mark_error'));
      } finally {
        this.marking = false;
      }
    },
  },
};
</script>

<style scoped>
/* 1단(좁은 화면): 미리보기 → 보정 → 정보. 미리보기는 스크롤해도 위에 고정해 보정 중 움직임이 보이게 한다.
   [함정] sticky는 조상에 overflow가 있으면 안 먹는다 — 스크롤 컨테이너는 .ant-modal-body(body-style) 하나뿐. */
.ccpm-body {
  /* body-style padding:0(sticky용)이라 좌우 여백을 여기서 준다 — 전역 모달 헤더·푸터와 같은 20px */
  padding: 16px 20px 0;
  display: grid;
  grid-template-columns: 100%;
  grid-template-areas: "preview" "calib" "info";
  column-gap: 28px;
}
.ccpm-preview-col {
  grid-area: preview;
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  align-self: start;
}
.ccpm-info-col { grid-area: info; min-width: 0; }
/* 2단에서는 정보 표 행을 낮춰(7행) 1366x768에서도 보정 폼이 더 많이 보이게 한다 */
.ccpm-body.is-wide .ccpm-info-col :deep(.ant-descriptions-row > th),
.ccpm-body.is-wide .ccpm-info-col :deep(.ant-descriptions-row > td) {
  padding: 5px 12px;
}
.ccpm-calib-col { grid-area: calib; min-width: 0; }
/* 2단(넓은 화면): 왼쪽 미리보기 고정, 오른쪽 위 정보·아래 보정 */
.ccpm-body.is-wide {
  grid-template-columns: 400px minmax(0, 1fr);
  grid-template-areas: "preview info" "preview calib";
  grid-template-rows: auto 1fr;
}
/* 2단에서만 미리보기 1.25배 — 안쪽 320px 좌표계는 그대로 두고 바깥 상자만 키운다(인쇄 계산 불변).
   높이 252.6px = 320 * (408/646) * 1.25 (참조 이미지 비율) */
.ccpm-preview-scale {
  width: 320px;
  max-width: 100%;
  margin: 0 auto;
}
.ccpm-body.is-wide .ccpm-preview-scale {
  width: 400px;
  height: 253px;
  margin-bottom: 6px;
}
.ccpm-body.is-wide .ccpm-preview-scale .card-preview-image-wrapper {
  transform: scale(1.25);
  transform-origin: top left;
  margin: 0;
}
.ccpm-section-title {
  font-size: 12px !important;
  margin: 16px 0 10px !important;
}
/* 2단에서 오른쪽 첫 제목을 왼쪽 첫 줄(卡片编号)과 같은 높이에 맞춘다 */
.ccpm-body.is-wide .ccpm-info-col > .ccpm-section-title {
  margin-top: 0 !important;
}
.ccpm-source-tag {
  margin-left: 8px;
  font-weight: normal;
}
/* X·Y 입력 + 방향 버튼 줄 */
/* 보정 영역 세로 간격은 블록마다 12px로 통일 */
.ccpm-offset-mode,
.ccpm-ctrl {
  margin-bottom: 12px;
}
.ccpm-ctrl-label {
  font-size: 12px;
  color: #595959;
  line-height: 20px;
  margin-bottom: 4px;
}
.ccpm-offset-input-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 12px;
}
.ccpm-offset-input-row .ccpm-ctrl {
  margin-bottom: 0;
}
.ccpm-offset-input {
  flex: 1;
  min-width: 0;
}
/* 방향 버튼 십자 배치 (↑ / ← → / ↓) — 높이를 라벨+입력칸(56px)에 맞춘다 */
.ccpm-nudge-pad {
  display: grid;
  grid-template-columns: repeat(3, 30px);
  grid-template-rows: repeat(3, 20px);
  gap: 2px;
  flex: none;
}
/* [함정] 전역 ant-design.scss의 .ant-btn 높이가 덮어쓰므로 !important */
.ccpm-nudge-pad .ant-btn {
  width: 30px !important;
  height: 20px !important;
  min-width: 0 !important;
  padding: 0 !important;
  font-size: 11px;
}
.ccpm-nudge-up { grid-column: 2; grid-row: 1; }
.ccpm-nudge-left { grid-column: 1; grid-row: 2; }
.ccpm-nudge-right { grid-column: 3; grid-row: 2; }
.ccpm-nudge-down { grid-column: 2; grid-row: 3; }
.ccpm-field {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
}
.ccpm-label {
  color: #666;
  font-size: 12px;
  min-width: 72px;
}
.ccpm-mono {
  font-family: monospace;
  font-weight: 600;
}
/* 버튼줄 — 스크롤 컨테이너(.ant-modal-body) 아래에 고정 */
.ccpm-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  position: sticky;
  bottom: 0;
  z-index: 3;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  /* 전역 .ant-modal-footer(10px 20px 16px)와 같은 여백 */
  padding: 10px 20px 16px;
  margin-top: 16px;
}
.ccpm-help-row {
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
}
/* 2단: 미리보기 아래 빈 공간에 삽입방향 안내 상시 노출(썸네일 + 설명) */
.ccpm-insert-guide {
  display: flex;
  gap: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  background: #fafafa;
  padding: 12px;
}
.ccpm-insert-guide-thumb {
  flex: none;
  width: 96px;
  height: 170px;
  object-fit: cover;
  border-radius: 4px;
  cursor: zoom-in;
}
.ccpm-insert-guide-body {
  min-width: 0;
}
.ccpm-insert-guide-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #434343;
  margin-bottom: 4px;
}
.ccpm-insert-guide .ccpm-help-text {
  font-size: 12px;
  line-height: 1.6;
  margin-bottom: 0;
}
.ccpm-btn-help-ico {
  margin-left: 2px;
  opacity: 0.55;
  font-size: 12px;
}

/* 이미지 기반 카드 뒷면 미리보기 — CertificateCardIssue.vue .card-preview-image-wrapper와 동일 규약(scoped라 복제) */
.card-preview-image-wrapper {
  position: relative;
  display: block;
  width: 320px;
  max-width: 100%;
  margin: 0 auto 6px;
  overflow: hidden;
}
.card-preview-image {
  width: 100%;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  display: block;
}
/* 오프셋(mm) 시각 피드백 레이어 — 우리가 인쇄하는 7필드만 함께 움직인다 */
.ccpm-overlay-shift {
  position: absolute;
  inset: 0;
  transition: transform 0.12s ease-out;
}
/* 카드지에 이미 인쇄되어 나오는 값(GTIN·No.) — 인쇄 대상이 아님을 점선 테두리로 구분한다 */
.overlay-preprinted {
  border: 1px dashed #bfbfbf;
  color: #8c8c8c !important;
  background: rgba(255, 255, 255, 0.85) !important;
}
.overlay-gtin {
  position: absolute;
  font-family: monospace;
  line-height: 1.1;
  padding: 1px 2px;
  border-radius: 2px;
  word-break: break-all;
  text-align: center;
}
.overlay-random-no {
  position: absolute;
  font-family: monospace;
  font-weight: bold;
  padding: 1px 4px;
  border-radius: 2px;
  white-space: nowrap;
}
.ccpm-preprinted-legend {
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
  font-size: 11px;
  color: #8c8c8c;
  margin-bottom: 16px;
}
.ccpm-preprinted-swatch {
  display: inline-block;
  width: 16px;
  height: 10px;
  border: 1px dashed #bfbfbf;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.85);
}
/* 작업지시서 7필드 — 참조 이미지 위 오버레이(왼쪽 정렬, 카드지 색과 무관하게 잘 보이도록
   반투명 흰 배경만 최소로 깐다). 실제 인쇄에는 배경이 없다(buildPrintHtml 참조) — 여기는 화면 가독용. */
.overlay-field {
  position: absolute;
  font-family: 'Microsoft YaHei', 'PingFang SC', sans-serif;
  font-weight: bold;
  font-size: 9px;
  color: #222;
  background: rgba(255, 255, 255, 0.75);
  padding: 0 2px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: pointer;
  /* 보정값을 바꾸면 글자가 부드럽게 움직여 어느 칸이 얼마나 갔는지 눈으로 따라갈 수 있다 */
  transition: transform 0.12s ease-out;
}
/* 개별 조정 중 선택한 칸 — 인쇄에는 영향 없음(미리보기 전용) */
.overlay-field-selected {
  outline: 1px solid #1677ff;
  background: rgba(22, 119, 255, 0.12);
}

/* 인쇄 위치 보정 — 보정 방식 안내·초기화 줄 */
.ccpm-offset-hint {
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 6px;
}
.ccpm-offset-reset-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.ccpm-offset-summary {
  font-size: 12px;
  color: #595959;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  padding: 4px 8px;
  margin-bottom: 12px;
  word-break: break-all;
}
.ccpm-offset-summary-title {
  font-weight: 600;
  margin-right: 4px;
}
.ccpm-offset-move-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.ccpm-offset-global-note {
  font-size: 12px;
  color: #8c8c8c;
}
.ccpm-offset-badge {
  margin-left: 6px;
  font-size: 12px;
  color: #1677ff;
}

/* 카드 삽입방향 도움말 */
.ccpm-help-text {
  font-size: 13px;
  color: #555;
  margin-bottom: 12px;
}
.ccpm-help-image {
  width: 100%;
  border-radius: 4px;
  display: block;
}
</style>
